/**
 * ============================================================
 * AdminPersonForm.js — Create/Edit Person
 * ============================================================
 *
 * Modes:
 *   - NEW:  /admin/people/new
 *   - EDIT: /admin/people/edit/:id
 * 
 * Loads:
 *   - Person record + linked projects/publications (GET /api/people/:id)
 *   - Groups list (GET /api/people/groups)
 *   - Institutions list (GET /api/people/institutions)
 *   - All projects (GET /api/projects)
 *   - All publications (GET /api/publications)
 * 
 * Saves:
 *   - Create person (POST /api/people)
 *   - Update person (PUT /api/people/:id)
 *   - Delete person (DELETE /api/people/:id)
 *
 * Notes:
 *   - Uses authFetch() for auto-redirect on token expiry
 **/

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";

import SynlabAdminNavbar from "../components/SynlabAdminNavbar";
import SynlabAdminFooter from "../components/SynlabAdminFooter";
import ItemList from "../components/ListItem";

import { authFetch } from "../../utils/authFetch";

import "./css/AdminSubpage.css";
import "./css/AdminForm.css";

import { personPhoto } from "../../utils/mediaPaths";

// ------------------------------------------------------------
// Component
// ------------------------------------------------------------
function AdminPersonForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = id && id !== "new";

  // Person state
  const [person, setPerson] = useState({
    firstname: "",
    lastname: "",
    title: "",
    shorttitle: "",
    startyear: new Date().getFullYear(),
    endyear: 0,
    email: "",
    institution_email: "",
    url: "",
    bio: "",
    group_id: "",
    institution_id: "",
    photo: "",
    unlisted: 0,    // stored as 0/1 on backend
    projects: [],
    publications: [],
  });

  // File upload
  const [file, setFile] = useState(null);

  // Lookups
  const [groups, setGroups] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [allPublications, setAllPublications] = useState([]);

  // Search inputs for add lists
  const [searchProjects, setSearchProjects] = useState("");
  const [searchPublications, setSearchPublications] = useState("");

  // Delete modal
  const [showDelete, setShowDelete] = useState(false);

  // ------------------------------------------------------------
  // Validate token on initial page load
  // ------------------------------------------------------------
  useEffect(() => {
    async function validateToken() {
      try {
        await authFetch("/api/authenticate");
        // If token is valid, nothing happens
      } catch (err) {
        // authFetch already handles redirect for 401/403
        console.error("Token validation failed:", err);
      }
    }
    validateToken();
  }, []);

  // ------------------------------------------------------------
  // Utility: always return an array
  // ------------------------------------------------------------
  function safeArray(value) {
    return Array.isArray(value) ? value : [];
  }

  // ------------------------------------------------------------
  // Loaders (person data + lookups)
  // ------------------------------------------------------------
  async function loadPerson(editing, id, navigate, setPerson) {
    // Load person data only if editing an existing person
    if (!editing) return;

    try {
      const res = await authFetch(`/api/people/${id}`);
      if (!res) return navigate("/admin/people");
      const data = await res.json();

      const persObj = data.personData || {};
      const projArr = safeArray(data.projectData);
      const pubArr = safeArray(data.publicationData);

      setPerson({
        ...persObj,
        projects: projArr.map((proj) => ({
          ...proj,
          position: proj.position ?? 0,
          faculty: !!proj.faculty,
          alumni: !!proj.alumni,
        })),
        publications: pubArr.map((pub) => ({
          ...pub,
          position: pub.position ?? 0,
        })),
      });

    } catch (err) {
      console.error("Failed to load person:", err);
      navigate("/admin/people");
    }
  }

  async function loadGroups(setGroups) {
    // Load groups data as an array
    try {
      const res = await authFetch("/api/people/groups");
      if (!res) return setGroups([]);
      const data = await res.json();
      setGroups(safeArray(data));
    } catch {
      setGroups([]);
    }
  }

  async function loadInstitutions(setInstitutions) {
    // Load institutions data as an array
    try {
      const res = await authFetch("/api/people/institutions");
      if (!res) return setInstitutions([]);
      const data = await res.json();
      setInstitutions(safeArray(data));
    } catch {
      setInstitutions([]);
    }
  }

  async function loadProjects(setAllProjects) {
    // Load projects data as an array
    try {
      const res = await authFetch("/api/projects");
      if (!res) return setAllProjects([]);
      const data = await res.json();
      setAllProjects(safeArray(data));
    } catch {
      setAllProjects([]);
    }
  }

  async function loadPublications(setAllPublications) {
    // Load publications data as an array
    try {
      const res = await authFetch("/api/publications");
      if (!res) return setAllPublications([]);
      const data = await res.json();
      setAllPublications(safeArray(data));
    } catch {
      setAllPublications([]);
    }
  }

  // ------------------------------------------------------------
  // Load all data
  // ------------------------------------------------------------
  useEffect(() => {
    async function loadData() {
      try {
        // Validate auth
        await authFetch("/api/authenticate");

        // Parallel-load all lookups
        await Promise.all([
          loadGroups(setGroups),
          loadInstitutions(setInstitutions),
          loadProjects(setAllProjects),
          loadPublications(setAllPublications),
        ]);

        // Load person only after lookups are ready
        await loadPerson(editing, id, navigate, setPerson);
      
      } catch (err) {
        console.error("AdminPersonForm load error:", err);
        if (editing) navigate("/admin/people");
      }
    }

    loadData();
  }, [editing, id, navigate]);

  // ------------------------------------------------------------
  // Helpers: form field update
  // ------------------------------------------------------------
  function updateField(field, value) {
    setPerson((prev) => ({ ...prev, [field]: value }));
  }

  function handleInputChange(e) {
    const { name, value, type, checked, files } = e.target;

    if (name === "photo") {
      if (files && files[0]) setFile(files[0]);
      return;
    }

    if (type === "checkbox") {
      updateField(name, checked ? 1 : 0);
      return;
    }

    updateField(name, value);
  }

  function removePhoto() {
    setPerson((prev) => ({ ...prev, photo: "" }));
    setFile(null);
  }

  // ------------------------------------------------------------
  // Filter projects/pubs to add (avoid duplicates)
  // ------------------------------------------------------------

  // Filtered list of projects
  const filteredProjects = useMemo(() => {
    const s = searchProjects.toLowerCase().trim();
    if (!s) return [];

    const matches = allProjects.filter((pr) =>
      (pr.name || "").toLowerCase().includes(s)
    );

    return matches.filter(
      (pr) => !person.projects.some((p) => p.id === pr.id)
    );
  }, [searchProjects, allProjects, person.projects]);

  // Filtered list of publications
  const filteredPublications = useMemo(() => {
    const s = searchPublications.toLowerCase().trim();
    if (!s) return [];

    const matches = allPublications.filter((pub) =>
      (pub.title || "").toLowerCase().includes(s)
    );

    return matches.filter(
      (pub) => !person.publications.some((p) => p.id === pub.id)
    );
  }, [searchPublications, allPublications, person.publications]);

  // ------------------------------------------------------------
  // Add/remove projects/publications
  // ------------------------------------------------------------

  // Add a project for this person
  function addProject(project) {
    const toAdd = {
      id: project.id,
      name: project.name,
      position: 0,
      faculty: false,
      alumni: false,
    };

    setPerson((prev) => ({
      ...prev,
      projects: prev.projects.concat(toAdd),
    }));
  }

  // Remove a project for this person
  function removeProject(projectId) {
    setPerson((prev) => ({
      ...prev,
      projects: prev.projects.filter((p) => p.id !== projectId),
    }));
  }

  // Update a project for this person
  function updateProject(projectId, patch) {
    setPerson((prev) => ({
      ...prev,
      projects: prev.projects.map((p) =>
        p.id === projectId ? { ...p, ...patch } : p
      ),
    }));
  }

  // Add a publication for this person
  function addPublication(pub) {
    const toAdd = {
      id: pub.id,
      title: pub.title,
      position: 0,
    };

    setPerson((prev) => ({
      ...prev,
      publications: prev.publications.concat(toAdd),
    }));
  }

  // Remove a publication for this person
  function removePublication(pubId) {
    setPerson((prev) => ({
      ...prev,
      publications: prev.publications.filter((p) => p.id !== pubId),
    }));
  }

  // Update a publication for this person
  function updatePublication(pubId, patch) {
    setPerson((prev) => ({
      ...prev,
      publications: prev.publications.map((p) =>
        p.id === pubId ? { ...p, ...patch } : p
      ),
    }));
  }

  // ------------------------------------------------------------
  // Submit (create/update)
  // ------------------------------------------------------------
  async function handleSubmit(e) {
    e.preventDefault();

    try {
      const formData = new FormData();

      // photo: new file wins, otherwise existing path
      if (file) formData.append("photo", file);
      else formData.append("photo", person.photo || "");

      // scalar fields
      const scalarFields = [
        "firstname", "lastname", "title", "shorttitle",
        "startyear", "endyear",
        "email", "institution_email",
        "url", "bio",
        "group_id", "institution_id",
        "unlisted",
      ];

      scalarFields.forEach((f) => {
        formData.append(f, person[f] ?? "");
      });

      // linked data
      formData.append("projects", JSON.stringify(person.projects || []));
      formData.append("publications", JSON.stringify(person.publications || []));

      const endpoint = editing
        ? `/api/people/${id}`
        : `/api/people`;

      const method = editing ? "PUT" : "POST";

      const res = await authFetch(endpoint, {
        method,
        body: formData,
      });

      if (!res) return;

      navigate("/admin/people");
    } catch (err) {
      console.error("AdminPeopleForm submit error:", err);
      alert("Failed to save person.");
    }
  }

  // ------------------------------------------------------------
  // Delete
  // ------------------------------------------------------------
  async function handleDelete() {
    try {
      const res = await authFetch(`/api/people/${id}`, {
        method: "DELETE",
      });

      if (!res) return;

      setShowDelete(false);
      navigate("/admin/people");
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete person.");
    }
  }

  // ----------------------------------------------------------
  // Get the photo path for display
  // ----------------------------------------------------------
  const photoSrc = personPhoto(person.photo);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div className="admin-wrapper">

      <SynlabAdminNavbar />

      <div className="admin-subpage-container">

        {/* ----- TITLE ----- */}
        <div className="admin-page-header">
          <h1 className="admin-page-title">
            {editing ? "Edit Person" : "Create Person"}
          </h1>
        </div>

        {/* ----- DESCRIPTION ----- */}
        <p className="admin-page-description">
          {editing
            ? "Update this person's record, photo, and linked projects/publications."
            : "Create a new person record for the Synlab people directory."}
        </p>

        {/* ----- FORM ----- */}
        <form onSubmit={handleSubmit} className="admin-form">

          {/* ----- BASIC INFO ----- */}
          <div className="admin-form-section">
            <h2 className="admin-section-title">Basic Info</h2>

            <div className="admin-form-grid">

              {/* ----- FIRST NAME ----- */}
              <div className="admin-form-field">
                <label className="form-label">First Name</label>
                <input
                  name="firstname"
                  className="form-control"
                  value={person.firstname || ""}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* ----- LAST NAME ----- */}
              <div className="admin-form-field">
                <label className="form-label">Last Name</label>
                <input
                  name="lastname"
                  className="form-control"
                  value={person.lastname || ""}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* ----- TITLE ----- */}
              <div className="admin-form-field">
                <label className="form-label">Full Title</label>
                <input
                  name="title"
                  className="form-control"
                  value={person.title || ""}
                  onChange={handleInputChange}
                />
              </div>

              {/* ----- SHORT TITLE ----- */}
              <div className="admin-form-field">
                <label className="form-label">Short Title</label>
                <input
                  name="shorttitle"
                  className="form-control"
                  value={person.shorttitle || ""}
                  onChange={handleInputChange}
                />
              </div>

              {/* ----- START YEAR ----- */}
              <div className="admin-form-field">
                <label className="form-label">Start Year</label>
                <input
                  name="startyear"
                  className="form-control"
                  value={person.startyear || ""}
                  onChange={handleInputChange}
                />
              </div>

              {/* ----- END YEAR ----- */}
              <div className="admin-form-field">
                <label className="form-label">End Year (0 = current)</label>
                <input
                  name="endyear"
                  className="form-control"
                  value={person.endyear ?? 0}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          {/* ----- CONTACT + BIO ----- */}
          <div className="admin-form-section">
            <h2 className="admin-section-title">Contact & Bio</h2>

            <div className="admin-form-grid">

              {/* ----- EMAIL ----- */}
              <div className="admin-form-field">
                <label className="form-label">Email</label>
                <input
                  name="email"
                  className="form-control"
                  value={person.email || ""}
                  onChange={handleInputChange}
                />
              </div>

              {/* ----- INSTITUTION EMAIL ----- */}
              <div className="admin-form-field">
                <label className="form-label">Institution Email</label>
                <input
                  name="institution_email"
                  className="form-control"
                  value={person.institution_email || ""}
                  onChange={handleInputChange}
                />
              </div>

              {/* ----- PERSONAL PAGE URL ----- */}
              <div className="admin-form-field admin-form-field-wide">
                <label className="form-label">Peronsal Page URL</label>
                <input
                  name="url"
                  className="form-control"
                  value={person.url || ""}
                  onChange={handleInputChange}
                />
              </div>

              {/* ----- BIO ----- */}
              <div className="admin-form-field admin-form-field-wide">
                <label className="form-label">Bio</label>
                <textarea
                  name="bio"
                  className="form-control"
                  rows={8}
                  value={person.bio || ""}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          {/* ----- GROUP / INSTITUTION ----- */}
          <div className="admin-form-section">
            <h2 className="admin-section-title">Affiliation</h2>

            <div className="admin-form-grid">

              {/* ----- GROUP ----- */}
              <div className="admin-form-field">
                <label className="form-label">Group</label>
                <select
                  name="group_id"
                  className="form-select"
                  value={person.group_id || ""}
                  onChange={handleInputChange}
                >

                  {/* ----- FALLBACK OPTION ----- */}
                  <option value="">-- Select Group --</option>

                  {safeArray(groups).map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* ----- INSTITUTION ----- */}
              <div className="admin-form-field">
                <label className="form-label">Institution</label>
                <select
                  name="institution_id"
                  className="form-select"
                  value={person.institution_id || ""}
                  onChange={handleInputChange}
                >

                  {/* ----- FALLBACK OPTION ----- */}
                  <option value="">-- Select Institution --</option>

                  {safeArray(institutions).map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* ----- UNLISTED ----- */}
              <div className="admin-form-field admin-checkbox-field admin-form-field-wide">
                <label className="form-label">Unlisted</label>
                <div className="admin-checkbox-inline">
                  <input
                    name="unlisted"
                    type="checkbox"
                    checked={!!person.unlisted}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ----- PHOTO ----- */}
          <div className="admin-form-section">
            <h2 className="admin-section-title">Photo</h2>

            {person.photo && !file ? (
              <div className="admin-photo-row">
                <img
                  src={photoSrc}
                  alt="person"
                  className="admin-photo-preview"
                />
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={removePhoto}
                >
                  Remove Photo
                </button>
              </div>
            ) : (
              <div className="admin-form-field">
                <label className="form-label">Upload Photo</label>
                <input
                  name="photo"
                  type="file"
                  accept="image/*"
                  className="form-control"
                  onChange={handleInputChange}
                />
              </div>
            )}
          </div>

          {/* ----- PROJECTS ----- */}
          <div className="admin-form-section">
            <h2 className="admin-section-title">Projects</h2>

            <div className="admin-form-field admin-form-field-wide">
              <label className="form-label">Search projects to add</label>
              <input
                className="form-control"
                value={searchProjects}
                onChange={(e) => setSearchProjects(e.target.value)}
                placeholder="Type to search projects..."
              />
            </div>

            {/* ----- ADDABLE LIST ----- */}
            <div className="admin-add-list">
              {safeArray(filteredProjects).length === 0 ? (
                <div className="admin-empty-list">No projects found</div>
              ) : (
                safeArray(filteredProjects).map((pr) => (
                  <ItemList
                    key={pr.id}
                    name={pr.name}
                    isAdd
                    backgroundColor="#f0f0f0"
                    onClick={() => addProject(pr)}
                  />
                ))
              )}
            </div>

            {/* ----- SELECTED PROJECTS ----- */}
            <div className="admin-selected-list">
              {safeArray(person.projects).map((pr) => (
                <ItemList
                  key={pr.id}
                  name={pr.name}
                  isDelete
                  onClick={() => removeProject(pr.id)}
                  edit={
                    <div className="admin-inline-edit">
                      <label>Position</label>
                      <input
                        className="form-control form-control-sm"
                        value={pr.position ?? 0}
                        onChange={(e) =>
                          updateProject(pr.id, { position: e.target.value })
                        }
                      />

                      <label>Faculty</label>
                      <input
                        type="checkbox"
                        checked={!!pr.faculty}
                        onChange={(e) =>
                          updateProject(pr.id, { faculty: e.target.checked })
                        }
                      />
                    </div>
                  }
                />
              ))}
            </div>
          </div>

          {/* ----- PUBLICATIONS ----- */}
          <div className="admin-form-section">
            <h2 className="admin-section-title">Publications</h2>

            <div className="admin-form-field admin-form-field-wide">
              <label className="form-label">Search publications to add</label>
              <input
                className="form-control"
                value={searchPublications}
                onChange={(e) => setSearchPublications(e.target.value)}
                placeholder="Type to search publications..."
              />
            </div>

            {/* ----- ADDABLE LIST ----- */}
            <div className="admin-add-list">
              {safeArray(filteredPublications).length === 0 ? (
                <div className="admin-empty-list">
                  No publications found
                </div>
              ) : (
                safeArray(filteredPublications).map((pub) => (
                  <ItemList
                    key={pub.id}
                    name={pub.title}
                    isAdd
                    backgroundColor="#f0f0f0"
                    onClick={() => addPublication(pub)}
                  />
                ))
              )}
            </div>

            {/* ----- SELECTED PUBLICATIONS ----- */}
            <div className="admin-selected-list">
              {safeArray(person.publications).map((pub) => (
                <ItemList
                  key={pub.id}
                  name={pub.title}
                  isDelete
                  onClick={() => removePublication(pub.id)}
                  edit={
                    <div className="admin-inline-edit">
                      <label>Position</label>
                      <input
                        className="form-control form-control-sm"
                        value={pub.position ?? 0}
                        onChange={(e) =>
                          updatePublication(pub.id, {
                            position: e.target.value,
                          })
                        }
                      />
                    </div>
                  }
                />
              ))}
            </div>
          </div>

          {/* ----- BUTTONS ----- */}
          <div className="admin-form-buttons">
            <button className="btn btn-primary" type="submit">
              {editing ? "Save Changes" : "Create Person"}
            </button>

            {editing && (
              <button
                className="btn btn-danger"
                type="button"
                onClick={() => setShowDelete(true)}
              >
                Delete Person
              </button>
            )}

            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => navigate("/admin/people")}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* ----- DELETE CONFIRM MODAL ----- */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Person</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this person? This cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <SynlabAdminFooter />
    </div>
  );
}

export default AdminPersonForm;